import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Sidebar from "../../components/MasterSideBar.jsx";
import ApplicantsCharts from "../../components/applicants/ApplicantsChart.jsx";
import AddApplicantForm from "../../components/applicants/AddApplicantForm.jsx";
import ApplicantsTable from "../../components/applicants/ApplicantsTable";
import BulkApplicantUploadModal from "../../components/applicants/BulkApplicantUploadModal.jsx";
import Topbar from "../../components/TopbarNav";
import { toast } from "react-toastify";
import {
  Users, UserCheck, CheckCircle2, XCircle, Search, Filter, Plus, 
  BarChart3, Upload, Loader2, Briefcase, TrendingUp, ArrowUpRight, 
  ArrowDownRight, FileText
} from "lucide-react";

const StatCard = ({ title, value, change, changeType, icon, iconBg, description }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change && (
          <div className={`flex items-center mt-2 text-sm ${changeType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
            {changeType === 'positive' ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
            <span>{change}</span>
          </div>
        )}
        {description && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
      <div className={`p-3 rounded-lg ${iconBg}`}>
        {icon}
      </div>
    </div>
  </div>
);

const Applicants = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [applicants, setApplicants] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [search, setSearch] = useState("");
  const token = localStorage.getItem("token");

  const fetchData = async () => {
    try {
      if (!token) return;
      const [appsRes, jobsRes] = await Promise.all([
        axios.get("https://ims.holdthechild.net/api/applicants", { headers: { Authorization: `Bearer ${token}` } }),
        axios.get("https://ims.holdthechild.net/api/job-posts", { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setApplicants(appsRes.data.data || appsRes.data || []);
      setJobs(jobsRes.data.data || jobsRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load applicants.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const normalizeStatus = (status = "") => status.toString().trim().toUpperCase();

  const totalApplicants = applicants.length;
  const shortlisted = applicants.filter(a => normalizeStatus(a.status) === "SHORTLISTED").length;
  const successful = applicants.filter(a => normalizeStatus(a.status) === "SUCCESSFUL").length;
  const unsuccessful = applicants.filter(a => normalizeStatus(a.status) === "UNSUCCESSFUL").length;

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          <span className="ml-3 text-gray-600">Loading data...</span>
        </div>
      );
    }

    if (activeTab === 'overview') {
      return (
        <div className="space-y-6">
          {/* Stats Bar */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard 
              title="Total Applicants" 
              value={totalApplicants} 
              icon={<Users className="text-blue-600" />} 
              iconBg="bg-blue-50"
              description="Total applications received"
            />
            <StatCard 
              title="Shortlisted" 
              value={shortlisted} 
              icon={<UserCheck className="text-orange-600" />} 
              iconBg="bg-orange-50"
              description="Candidates for interview"
            />
            <StatCard 
              title="Successful" 
              value={successful} 
              icon={<CheckCircle2 className="text-green-600" />} 
              iconBg="bg-green-50"
              description="Hired candidates"
            />
            <StatCard 
              title="Unsuccessful" 
              value={unsuccessful} 
              icon={<XCircle className="text-red-600" />} 
              iconBg="bg-red-50"
              description="Rejected applications"
            />
          </div>

          {/* Table Container */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-50 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800 tracking-tight">Application List</h2>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-orange-700 transition-all shadow-md shadow-orange-100"
                >
                  <Plus className="w-4 h-4" /> Add Applicant
                </button>
                <button
                  onClick={() => setShowBulkModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-gray-50 transition-all"
                >
                  <Upload className="w-4 h-4" /> Bulk Import
                </button>
              </div>
            </div>
            <ApplicantsTable
              applicants={applicants}
              jobs={jobs}
              loading={loading}
              onRefresh={fetchData}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-[400px]">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Application Status</h3>
            <ApplicantsCharts applicants={applicants} type="status" />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-[400px]">
             <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Source of App</h3>
            <ApplicantsCharts applicants={applicants} type="source" />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-[400px]">
             <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Apps by Job</h3>
            <ApplicantsCharts applicants={applicants} type="jobs" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      <Sidebar onLogout={handleLogout} />

      <div className="flex-1 relative overflow-y-auto md:ml-64">
        <div className="fixed top-0 left-0 md:left-64 right-0 z-10">
          <Topbar />
        </div>

        <main className="pt-20 px-4 md:px-8 pb-12">
          {/* Header */}
          <div className="mb-10 text-center">
            <h1 className="text-4xl font-black text-gray-900 mb-2 uppercase tracking-tighter">
              Applicants <span className="text-orange-600">Dashboard</span>
            </h1>
            <p className="text-gray-500 font-medium italic">Track, manage and analyze your recruitment pipeline.</p>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-white rounded-2xl shadow-sm p-1 mb-8 border border-gray-100 inline-flex">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 py-3 px-8 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${
                activeTab === 'overview' 
                ? 'bg-orange-600 text-white shadow-lg shadow-orange-200' 
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Users className="w-3.5 h-3.5" /> Overview
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`flex items-center gap-2 py-3 px-8 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${
                activeTab === 'analytics' 
                ? 'bg-orange-600 text-white shadow-lg shadow-orange-200' 
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
            >
              <BarChart3 className="w-3.5 h-3.5" /> Analytics
            </button>
          </div>

          {/* Render Active Tab Content */}
          {renderTabContent()}

          {/* Modals */}
          {showAddModal && (
            <AddApplicantForm
              onClose={() => setShowAddModal(false)}
              onSuccess={() => {
                setShowAddModal(false);
                fetchData();
              }}
            />
          )}

          {showBulkModal && (
            <BulkApplicantUploadModal
              closeModal={() => setShowBulkModal(false)}
              jobs={jobs}
              onApplicantsUploaded={fetchData}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default Applicants;
// import React, { useEffect, useState, useMemo } from "react"; 
// import axios from "axios";
// import moment from "moment";
// import Sidebar from "../../components/Sidebar.jsx";
// import Card from "../../components/card.jsx";
// import ApplicantsCharts from "../../components/applicants/ApplicantsChart.jsx";
// import AddApplicantForm from "../../components/applicants/AddApplicantForm.jsx";
// import ApplicantsTable from "../../components/applicants/ApplicantsTable";
// import BulkApplicantUploadModal from "../../components/applicants/BulkApplicantUploadModal.jsx";
// import Topbar from "../../components/TopbarNav";
// import { FaUsers, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
// import { X } from "lucide-react";
// import { toast } from "react-toastify";

// const Applicants = () => {
//   const [applicants, setApplicants] = useState([]);
//   const [jobs, setJobs] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [showAddModal, setShowAddModal] = useState(false);
//   const [showBulkModal, setShowBulkModal] = useState(false);
//   const [search, setSearch] = useState("");
//   const token = localStorage.getItem("token");

  
//   const fetchData = async () => {
//     try {
//       if (!token) return;
//       const [appsRes, jobsRes] = await Promise.all([
//         // Use absolute URLs if relative ones are failing
//         axios.get("https://ims.holdthechild.net/api/applicants", { headers: { Authorization: `Bearer ${token}` } }),
//         axios.get("https://ims.holdthechild.net/api/job-posts", { headers: { Authorization: `Bearer ${token}` } }),
//       ]);
      
//       // Check if data is inside .data or .data.data based on your controller
//       setApplicants(appsRes.data.data || appsRes.data || []);
//       setJobs(jobsRes.data.data || jobsRes.data || []);
//     } catch (err) {
//       console.error(err);
//       toast.error("Failed to load applicants.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []);

//   /* Metrics (safe normalization) */
//   const normalizeStatus = (status = "") =>
//     status.toString().trim().toUpperCase();

//   const totalApplicants = applicants.length;

//   const shortlisted = applicants.filter(
//     a => normalizeStatus(a.status) === "SHORTLISTED"
//   ).length;

//   const successful = applicants.filter(
//     a => normalizeStatus(a.status) === "SUCCESSFUL"
//   ).length;

//   const unsuccessful = applicants.filter(
//     a => normalizeStatus(a.status) === "UNSUCCESSFUL"
//   ).length;

//   const filteredApplicants = useMemo(() => {
//     const q = search.toLowerCase();
//     return applicants.filter(a =>
//       a.fullName?.toLowerCase().includes(q) ||
//       a.email?.toLowerCase().includes(q) ||
//       a.job?.vacancy?.toLowerCase().includes(q)
//     );
//   }, [search, applicants]);

//   const handleLogout = () => {
//     localStorage.removeItem("token");
//     window.location.href = "/";
//   };

//   return (
//     <div className="flex min-h-screen w-full bg-gray-50 font-sans">
//       {/* Sidebar */}
//       <Sidebar onLogout={handleLogout} />

//       {/* Main Content */}
//       <div className="flex-1 relative overflow-y-auto md:ml-64">
//         {/* Topbar */}
//         <div className="fixed top-0 left-0 md:left-64 right-0 z-10">
//           <Topbar />
//         </div>

//         {/* Page Content */}
//         <main className="pt-20 px-4 md:px-6 space-y-6">
//           {/* Header */}
//           <div className="flex justify-between items-center">
//             <h1 className="text-2xl font-bold">Applicants Dashboard</h1>
//             <div className="flex gap-2">
//               <button
//                 className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors"
//                 onClick={() => setShowAddModal(true)}
//               >
//                 Add Applicant
//               </button>
//               <button
//                 className="border border-orange-500 text-orange-500 px-4 py-2 rounded hover:bg-orange-50 transition-colors"
//                 onClick={() => setShowBulkModal(true)}
//               >
//                 Bulk Add
//               </button>
//             </div>
//           </div>

//           {/* Add Applicant Modal */}
//           {showAddModal && (
//             <AddApplicantForm
//               onClose={() => setShowAddModal(false)}
//               onSuccess={() => {
//                 setShowAddModal(false);
//                 fetchData();
//               }}
//             />
//           )}

//           {/* Bulk Applicant Modal */}
//           {showBulkModal && (
//             <BulkApplicantUploadModal
//               closeModal={() => setShowBulkModal(false)}
//               jobs={jobs} // Added this prop
//               onApplicantsUploaded={fetchData}
//             />
//           )}

//           {/* Stats */}
//           <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
//             <Card title="Total Applicants" value={totalApplicants} icon={<FaUsers />} />
//             <Card title="Shortlisted" value={shortlisted} icon={<FaUsers />} />
//             <Card title="Successful" value={successful} icon={<FaCheckCircle />} />
//             <Card title="Unsuccessful" value={unsuccessful} icon={<FaTimesCircle />} />
//           </div>

//           {/* Charts */}
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div className="bg-white rounded shadow p-4 h-[320px]">
//               <ApplicantsCharts applicants={applicants} type="status" />
//             </div>

//             <div className="bg-white rounded shadow p-4 h-[320px]">
//               <ApplicantsCharts applicants={applicants} type="source" />
//             </div>

//             <div className="bg-white rounded shadow p-4 h-[320px]">
//               <ApplicantsCharts applicants={applicants} type="jobs" />
//             </div>
//           </div>

//           <ApplicantsTable
//             applicants={applicants}
//             loading={loading}
//             onRefresh={fetchData}
//           />
//         </main>
//       </div>
//     </div>
//   );
// };

// export default Applicants;

